/**
 * Where the chat socket connects.
 *
 * ── A THIRD BASE URL, AND THAT IS NOT AN OVERSIGHT ──────────────────────────
 * `D09` splits the backend into two Workers, for a reason that shows up here:
 * a Worker declaring Durable Objects gets NO per-branch preview URL, so the
 * agent had to live somewhere the API does not. They are different origins and
 * they are deployed independently, so a client that derived one from the other
 * would be correct until the first branch preview and wrong from then on —
 * pointing the socket at a host that answers 404 for `/chat` while every HTTP
 * call still works, which reads as "chat is broken" and nothing else.
 *
 * `api-base-url.ts` is the same idea for `apps/api` and `clips-base-url.ts` for
 * the R2 bucket. Three hosts, three variables, none derived from another.
 *
 * ── THE BASE IS THE APP'S, SINCE THE MOVE INTO `@repo/client` ───────────────
 * This is the pure half of `apps/mobile/src/config/ai-base-url.ts`. The other
 * half — `DEFAULT_AI_BASE_URL`, read from a build-time environment variable —
 * is a build input of whichever app bundles this package, so it stays with the
 * app and arrives here as `base`, which is therefore required. The fallback
 * that used to live here was `http://localhost:8788`, not `8787`: `wrangler
 * dev` gives the second Worker in a repository the next port, and the API's
 * port answers `/chat` with a 404 that does not name the port as the problem.
 *
 * ── WHY THE `ws:` CONVERSION IS HERE AND NOT AT THE CALL SITE ───────────────
 * React Native's `WebSocket` accepts an `http:` URL and connects, which is the
 * trap: it works in development against `wrangler dev` and then fails on the
 * first HTTPS deployment, because a page served over TLS may not open a
 * cleartext socket. Converting in one place means the development build and the
 * release build exercise the same line.
 */
// ── IMPORTED, NOT RESTATED, AND THAT IS NEW ─────────────────────────────────
// These three used to be spelled out here with a comment explaining that they
// had to be: they lived in `apps/ai`, and this bundle cannot import a workerd
// Worker that drags `agents`, `ai` and the Anthropic SDK behind it. A `?raw`
// test read that Worker's source and asserted the strings matched, because
// drift here is silent and points the wrong way — a misspelled parameter reads
// to the Worker as "no ticket presented", which is a 401 identical to a forged
// one, on a client that looks entirely correct.
//
// `D44`-era work moved the ticket contract into `@repo/contract`, which this
// bundle already depends on. One declaration, checked by `tsc`, so both the
// restatement and the guard that watched it are gone. A drift guard is only
// worth its weight while two copies exist.
import { CHAT_CONVERSATION_PARAM, CHAT_ROUTE, CHAT_SUBJECT_PARAM, CHAT_TICKET_PARAM, } from "@repo/contract/iam";
/** Hoisted: a literal inside the function is recompiled on every call. */
const TRAILING_SLASHES = /\/+$/;
/**
 * A table rather than a chain of ternaries, and exhaustive on purpose: the
 * ONLY two schemes this app may open a socket from are the two an origin can
 * legitimately have. Anything absent from it gets `undefined`, which is the
 * refusal below.
 */
const WS_SCHEME = {
    "http:": "ws:",
    "https:": "wss:",
};
/**
 * `http:` → `ws:`, `https:` → `wss:`, anything else → `null`.
 *
 * Refusing rather than passing an unknown scheme through: a `file:` or a bare
 * host reaches `new WebSocket()` as a string it cannot parse, and the failure
 * arrives asynchronously as a socket `error` event with no message — which the
 * screen would show as "could not connect", indistinguishable from being
 * offline.
 */
export function toWebSocketOrigin(httpUrl) {
    let parsed;
    try {
        parsed = new URL(httpUrl);
    }
    catch {
        return null;
    }
    const scheme = WS_SCHEME[parsed.protocol];
    if (scheme === undefined) {
        return null;
    }
    return `${scheme}//${parsed.host}${parsed.pathname}`.replace(TRAILING_SLASHES, "");
}
/**
 * The URL for one connection attempt.
 *
 * ── THE TICKET IS IN THE QUERY STRING, ONCE, AND IS NEVER REUSED ────────────
 * `D34`: a WebSocket handshake cannot carry a header, so the credential goes
 * in the URL and is made survivable by being opaque, single-use and 60 seconds
 * old at most. Consumption on the far side deletes it before any decision, so a
 * second use of the same ticket is a 401 indistinguishable from a forged one.
 * Every caller of this function must therefore have just bought the ticket it
 * passes.
 *
 * Returns `null` for a base URL that is not http(s), for the reason
 * `toWebSocketOrigin` refuses: a malformed URL fails later, asynchronously and
 * anonymously.
 */
export function chatSocketUrl(options) {
    const origin = toWebSocketOrigin(options.base);
    if (origin === null) {
        return null;
    }
    const query = new URLSearchParams({
        [CHAT_TICKET_PARAM]: options.ticket,
        [CHAT_CONVERSATION_PARAM]: options.conversationId,
    });
    if (options.subjectAccountId !== undefined) {
        query.set(CHAT_SUBJECT_PARAM, options.subjectAccountId);
    }
    return `${origin}${CHAT_ROUTE}?${query.toString()}`;
}
//# sourceMappingURL=ai-base-url.js.map